// The Risk Areas (New Experience) data layer: one RTK Query API covering every
// read and write the page performs.
//
// Transport is NOT fetchBaseQuery. Reads go through callEnterpriseApi (v1 API,
// okta token, timeout), writes through the same-origin legacy MVC endpoints the
// classic grid posts to. Both live in ./enterprise-api and
// ./legacy-lookups-service, so every endpoint here is a queryFn delegating to
// them over a fake base query.
//
// One `RiskArea` tag: every list, header, KPI and count query provides it, and
// every mutation invalidates it, refreshing the grid and every count in one step.
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { DEFAULT_PAGE_SIZE, RiskView } from "../constants";
import {
  ActiveUser,
  BillingHeader,
  BillingKpis,
  GroupChoice,
  HeaderParams,
  KpiParams,
  LinkedAudit,
  ListParams,
  OutcomeOption,
  PagedResult,
  ProviderCoderHeader,
  ProviderCoderKpis,
  RiskArea,
  RiskAreaBulkUpdateValues,
  RiskAreaDetailsDto,
  RiskAreaStatus,
  RiskAreaUpdate,
  SectionListArg,
} from "../types";
import {
  fetchBillingHeaders,
  fetchBillingKpis,
  fetchBillingRisks,
} from "./billing-risks-service";
import {
  fetchCoderRisks,
  fetchProviderCoderHeaders,
  fetchProviderCoderKpis,
  fetchProviderRisks,
} from "./provider-coder-risks-service";
import { fetchStatusCounts } from "./status-counts-service";
import {
  RiskAreaAuditInfoSnapshot,
  bulkDeleteRiskAreas,
  bulkUpdateRiskAreas,
  getActiveUsers,
  getBulkOutcomeOptions,
  getBulkStatusOptions,
  getCoderGroups,
  getOutcomeOptions,
  getProviderGroups,
  getRiskAreaAuditInfoSnapshot,
  getRiskAreaDetails,
  getRisksLinkedAudits,
  getStatusOptions,
  updateRiskArea,
} from "./legacy-lookups-service";

// What a failed request surfaces to the components. callMdaeApi rejects with the
// raw fetch Response on 401/500 and the timeout helper rejects with a tagged
// Error, so both shapes are flattened to the same three fields.
export interface RiskAreaApiError {
  status: number;
  statusText: string;
  message?: string;
}

const toApiError = (err: unknown): RiskAreaApiError => {
  const e = err as any;
  return {
    status: e?.status ?? 0,
    statusText: e?.statusText ?? "error",
    message: e?.message,
  };
};

// Adapts a plain service promise to RTK Query's { data } | { error } result.
// Every endpoint below is one call to this, so the try/catch isn't repeated.
const run = async <T>(
  call: () => Promise<T>
): Promise<{ data: T } | { error: RiskAreaApiError }> => {
  try {
    return { data: await call() };
  } catch (err) {
    return { error: toApiError(err) };
  }
};

// Where a section's next page starts, derived from how many unique rows it
// already holds rather than the last page number requested. These diverge once
// a row leaves the section (e.g. marked Satisfactory), shifting everything after
// it down one — `lastPageParam + 1` would then skip the row that moved into the
// gap. Re-requesting the current last page instead means overlap, which
// use-section-rows dedupes.
//
// `undefined` tells RTK Query there is no next page — this is what backs
// `hasNextPage` on the hook.
const nextPageFromLoaded = (
  lastPage: PagedResult<RiskArea>,
  allPages: PagedResult<RiskArea>[]
): number | undefined => {
  if (!lastPage.hasNext) return undefined;
  const seen = new Set<number>();
  allPages.forEach((page) =>
    page.items.forEach((row) => seen.add(row.riskAreaDetailId))
  );
  const pageSize = lastPage.pageSize || DEFAULT_PAGE_SIZE;
  return Math.floor(seen.size / pageSize) + 1;
};

const SECTION_PAGE_OPTIONS = {
  initialPageParam: 1,
  getNextPageParam: nextPageFromLoaded,
};

export const riskAreasApi = createApi({
  reducerPath: "riskAreasApi",
  baseQuery: fakeBaseQuery<RiskAreaApiError>(),
  // Two tags so rows and counts can be invalidated independently: headers, KPIs,
  // status counts and facets provide `RiskArea`; the per-section row queries
  // provide `RiskAreaRows`.
  tagTypes: ["RiskArea", "RiskAreaRows"],
  // Re-request on every mount rather than render a cached page. The cache still
  // dedupes concurrent subscribers and serves the previous rows while a refetch
  // is in flight.
  refetchOnMountOrArgChange: true,
  endpoints: (build) => ({
    // ── Billing Risks tab ────────────────────────────────────────────────────
    getBillingRisks: build.query<PagedResult<RiskArea>, ListParams>({
      queryFn: (params) => run(() => fetchBillingRisks(params)),
      providesTags: ["RiskArea"],
    }),
    getBillingHeaders: build.query<BillingHeader[], HeaderParams>({
      queryFn: (params) => run(() => fetchBillingHeaders(params)),
      providesTags: ["RiskArea"],
    }),
    getBillingKpis: build.query<BillingKpis | null, KpiParams>({
      queryFn: (params) => run(() => fetchBillingKpis(params)),
      providesTags: ["RiskArea"],
    }),

    // ── New Provider & Coder Risks tab ───────────────────────────────────────
    getProviderRisks: build.query<PagedResult<RiskArea>, ListParams>({
      queryFn: (params) => run(() => fetchProviderRisks(params)),
      providesTags: ["RiskArea"],
    }),
    getCoderRisks: build.query<PagedResult<RiskArea>, ListParams>({
      queryFn: (params) => run(() => fetchCoderRisks(params)),
      providesTags: ["RiskArea"],
    }),
    getProviderCoderHeaders: build.query<ProviderCoderHeader[], HeaderParams>({
      queryFn: (params) => run(() => fetchProviderCoderHeaders(params)),
      providesTags: ["RiskArea"],
    }),
    getProviderCoderKpis: build.query<ProviderCoderKpis | null, KpiParams>({
      queryFn: (params) => run(() => fetchProviderCoderKpis(params)),
      providesTags: ["RiskArea"],
    }),

    // ── Per-section rows (one accumulating cache entry per section) ──────────
    // The arg is the section's scope (a list call minus the page), so two
    // sections are two cache entries while every page of one section accumulates
    // into a single entry. These sit alongside the plain list queries above
    // rather than replacing them: those still serve the facet-only request,
    // which wants exactly one page and the facet block, not accumulation.
    getBillingSectionRows: build.infiniteQuery<
      PagedResult<RiskArea>,
      SectionListArg,
      number
    >({
      infiniteQueryOptions: SECTION_PAGE_OPTIONS,
      queryFn: ({ queryArg, pageParam }) =>
        run(() => fetchBillingRisks({ ...queryArg, page: pageParam })),
      providesTags: ["RiskAreaRows"],
    }),
    getProviderSectionRows: build.infiniteQuery<
      PagedResult<RiskArea>,
      SectionListArg,
      number
    >({
      infiniteQueryOptions: SECTION_PAGE_OPTIONS,
      queryFn: ({ queryArg, pageParam }) =>
        run(() => fetchProviderRisks({ ...queryArg, page: pageParam })),
      providesTags: ["RiskAreaRows"],
    }),
    getCoderSectionRows: build.infiniteQuery<
      PagedResult<RiskArea>,
      SectionListArg,
      number
    >({
      infiniteQueryOptions: SECTION_PAGE_OPTIONS,
      queryFn: ({ queryArg, pageParam }) =>
        run(() => fetchCoderRisks({ ...queryArg, page: pageParam })),
      providesTags: ["RiskAreaRows"],
    }),

    // ── Status tab badges ────────────────────────────────────────────────────
    // One entry per view, each a single groupBy=Status header call covering
    // every status bucket at once. Its own cache entry, not a slice of the
    // accordion's header data — these are one-row-per-bucket aggregates.
    getStatusCounts: build.query<
      Record<string, number>,
      { view: RiskView; base: HeaderParams }
    >({
      queryFn: ({ view, base }) => run(() => fetchStatusCounts(view, base)),
      providesTags: ["RiskArea"],
    }),

    // ── Legacy MVC reads (same endpoints as the classic grid) ────────────────
    // The option lists behind the per-row Status / Set Outcome / Assign To
    // controls, the bulk update modal and the two Group filters. All are
    // client-wide and don't vary by tab or quarter, so one cache entry each.
    getStatusOptions: build.query<RiskAreaStatus[], void>({
      queryFn: () => run(getStatusOptions),
    }),
    getOutcomeOptions: build.query<OutcomeOption[], void>({
      queryFn: () => run(getOutcomeOptions),
    }),

    // The bulk update modal's Status / Outcome lists, narrowed to the options
    // valid for every type in the selection. Keyed on the type list, so
    // re-opening the modal on the same mix is free.
    getBulkStatusOptions: build.query<RiskAreaStatus[], string[]>({
      queryFn: (riskAreaTypes) => run(() => getBulkStatusOptions(riskAreaTypes)),
    }),
    getBulkOutcomeOptions: build.query<OutcomeOption[], string[]>({
      queryFn: (riskAreaTypes) => run(() => getBulkOutcomeOptions(riskAreaTypes)),
    }),
    getActiveUsers: build.query<ActiveUser[], void>({
      queryFn: () => run(getActiveUsers),
    }),
    getProviderGroups: build.query<GroupChoice[], void>({
      queryFn: () => run(getProviderGroups),
    }),
    getCoderGroups: build.query<GroupChoice[], void>({
      queryFn: () => run(getCoderGroups),
    }),

    // The classic grid's own list payload, read for what the v1 rows don't
    // carry: each row's linked-audit count and the E&M bell curve parameters.
    getAuditInfoSnapshot: build.query<RiskAreaAuditInfoSnapshot, { isMyRiskAreas: boolean; quarter: number }>({
      queryFn: ({ isMyRiskAreas, quarter }) => run(() => getRiskAreaAuditInfoSnapshot(isMyRiskAreas, quarter)),
    }),

    // Per-row popups. Keyed by riskAreaDetailId, so a slow response for a
    // previous row can never land under the row currently open.
    getLinkedAudits: build.query<LinkedAudit[], number>({
      queryFn: (riskAreaDetailId) => run(() => getRisksLinkedAudits(riskAreaDetailId)),
    }),
    getRiskAreaDetails: build.query<RiskAreaDetailsDto | null, number>({
      queryFn: (riskAreaDetailId) => run(() => getRiskAreaDetails(riskAreaDetailId)),
    }),

    // ── Writes (legacy MVC, same endpoints as the classic grid) ──────────────
    // A single row's Status / Set Outcome / Assign To / Weight edit. Invalidates
    // both tags, which refetches every loaded page of the edited section — a
    // correct but not surgical response to a one-row change.
    updateRiskArea: build.mutation<null, RiskAreaUpdate>({
      queryFn: (payload) => run(async () => { await updateRiskArea(payload); return null; }),
      invalidatesTags: ["RiskArea", "RiskAreaRows"],
    }),

    // The bulk bar's Update — one BulkEditRiskArea call per batch of ids, not one
    // UpdateRiskArea call per row. The server merges the filled-in fields over
    // each row and leaves blanks alone, so nothing is read back and echoed.
    //
    // Batching exists because the id list lands in a SQL `IN (...)`; a selection
    // under BULK_EDIT_BATCH_SIZE is one request. Counts are weighted by batch
    // size so a mid-run failure still reports how many rows landed.
    bulkUpdateRiskAreas: build.mutation<
      { successCount: number; failCount: number },
      { batches: number[][]; values: RiskAreaBulkUpdateValues }
    >({
      queryFn: async ({ batches, values }) => {
        let successCount = 0;
        let failCount = 0;
        for (const ids of batches) {
          try {
            const updated = await bulkUpdateRiskAreas({
              ...values,
              operation: "UPDATE",
              riskAreaDetailIds: ids.join(","),
            });
            if (updated) successCount += ids.length;
            else failCount += ids.length;
          } catch (e) {
            failCount += ids.length;
          }
        }
        return { data: { successCount, failCount } };
      },
      invalidatesTags: ["RiskArea", "RiskAreaRows"],
    }),

    // The bulk bar's Delete. The endpoint answers false (not an error) when the
    // user lacks access to delete one of them, and nothing changed server-side
    // in that case — so that answer must not invalidate the cache.
    bulkDeleteRiskAreas: build.mutation<{ deleted: boolean }, number[]>({
      queryFn: (ids) => run(async () => ({ deleted: await bulkDeleteRiskAreas(ids) })),
      invalidatesTags: (result) =>
        result?.deleted ? ["RiskArea", "RiskAreaRows"] : [],
    }),
  }),
});

export const {
  useGetBillingRisksQuery,
  useGetBillingSectionRowsInfiniteQuery,
  useGetProviderSectionRowsInfiniteQuery,
  useGetCoderSectionRowsInfiniteQuery,
  useGetBillingHeadersQuery,
  useGetBillingKpisQuery,
  useGetProviderRisksQuery,
  useGetCoderRisksQuery,
  useGetProviderCoderHeadersQuery,
  useGetProviderCoderKpisQuery,
  useGetStatusCountsQuery,
  useGetStatusOptionsQuery,
  useGetOutcomeOptionsQuery,
  useGetBulkStatusOptionsQuery,
  useGetBulkOutcomeOptionsQuery,
  useGetActiveUsersQuery,
  useGetProviderGroupsQuery,
  useGetCoderGroupsQuery,
  useGetAuditInfoSnapshotQuery,
  useGetLinkedAuditsQuery,
  useGetRiskAreaDetailsQuery,
  useUpdateRiskAreaMutation,
  useBulkUpdateRiskAreasMutation,
  useBulkDeleteRiskAreasMutation,
} = riskAreasApi;
